// Vista ampliada de un adjunto de la caja de prompt —
// ADR-2026-09-08-los-adjuntos-de-la-caja-dicen-que-son-y-se-abren.
//
// El chip es de 76px: alcanza para acordarse de qué enganchaste, no para decidir si es
// LA foto correcta. Acá se abre grande, y en el caso de un producto con las OTRAS fotos
// de la variante —que es el caso donde el chip más miente: muestra la portada, y el
// usuario puede haber enganchado el producto pensando en otra de sus fotos.
//
// Es de sólo mirar: no elige la foto que se manda ni edita nada. La referencia que viaja
// a la generación la sigue resolviendo `resolverReferencias`, que toma la principal.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import type { ChatAttachment } from '../../agentAttachments';
import {
  etiquetaDeAdjunto,
  miniaturaDeAdjunto,
  tituloDeAdjunto,
} from '../../etiquetaDeAdjunto';
import { fetchProductImageUrls } from '../../productsApi';

export interface VisorDeAdjuntoProps {
  /** El adjunto abierto, o `null` cerrado. */
  adjunto: ChatAttachment | null;
  onClose: () => void;
  /** Empresa, para firmar las fotos de la variante. */
  companyId: string | null;
}

/**
 * Las fotos que muestra un adjunto.
 *
 * Un producto tiene varias y hay que ir a buscarlas; el resto trae la suya en el propio
 * adjunto. Arranca con la que ya se conoce (la del chip, que suele estar en caché del
 * navegador) así la vista abre CON algo y las demás entran cuando llegan: pedir primero
 * y dibujar después dejaría un rectángulo vacío por una foto que ya se está viendo en el
 * chip.
 */
function useFotosDelAdjunto(
  adjunto: ChatAttachment | null,
  companyId: string | null,
): { fotos: string[]; cargando: boolean } {
  const inmediata =
    adjunto == null ? null : miniaturaDeAdjunto(adjunto);
  const [extra, setExtra] = useState<string[] | null>(null);
  const [cargando, setCargando] = useState(false);

  const productId = adjunto?.kind === 'product' ? adjunto.productId : null;
  useEffect(() => {
    setExtra(null);
    if (!productId || !companyId) return;
    let cancelado = false;
    setCargando(true);
    fetchProductImageUrls(companyId, productId)
      .then((urls) => {
        if (!cancelado) setExtra(urls);
      })
      // Falla cerrado: se queda con la foto del chip, que es mejor que un error para
      // algo que el usuario abrió sólo para mirar.
      .catch(() => {
        if (!cancelado) setExtra(null);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [productId, companyId]);

  // Las de la variante mandan cuando llegaron (vienen en el orden de la ficha e incluyen
  // la principal en tamaño full); la del chip queda de puente mientras viajan.
  const fotos = extra && extra.length > 0 ? extra : inmediata ? [inmediata] : [];
  return { fotos, cargando };
}

export const VisorDeAdjunto: React.FC<VisorDeAdjuntoProps> = ({
  adjunto,
  onClose,
  companyId,
}) => {
  const { fotos, cargando } = useFotosDelAdjunto(adjunto, companyId);
  const [indice, setIndice] = useState(0);
  // Cambiar de adjunto vuelve a la primera foto: el índice del anterior no significa
  // nada acá y puede quedar fuera de rango.
  useEffect(() => setIndice(0), [adjunto?.id]);

  // Escape cierra. Se engancha sólo con el visor abierto para no dejar un listener
  // global escuchando todas las teclas de la app.
  useEffect(() => {
    if (!adjunto) return;
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', alTeclado);
    return () => window.removeEventListener('keydown', alTeclado);
  }, [adjunto, onClose]);

  if (!adjunto) return null;
  // `indice` puede quedar corto un frame si la lista se acorta: se acota al dibujar.
  const foto = fotos[Math.min(indice, Math.max(0, fotos.length - 1))] ?? null;

  return ReactDOM.createPortal(
    <>
      {/* Portal al `body` y `z-[220]`: el sheet del modo es `fixed` y arranca a la
          derecha de la sidebar, así que un overlay montado adentro quedaría recortado a
          esa caja. 220 es la familia que ya usan los pickers (210/211), un escalón más
          arriba para poder abrirse por encima de ellos. */}
      <div
        className="fixed inset-0 z-[220] bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={tituloDeAdjunto(adjunto)}
        className="fixed inset-0 z-[221] flex flex-col items-center justify-center p-6 pointer-events-none"
      >
        <div className="pointer-events-auto flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          {/* Encabezado: el nombre propio si lo hay, y debajo qué es. Los dos datos
              juntos porque "Top negro" solo no dice si lo enganchaste como producto o
              como imagen suelta, que es justo la confusión que abrió este cambio. */}
          <div className="flex shrink-0 items-start justify-between gap-3 px-5 py-4">
            <div className="min-w-0">
              <p className="truncate text-[15.5px] font-semibold leading-tight text-brand-black">
                {tituloDeAdjunto(adjunto)}
              </p>
              <p className="mt-0.5 text-[12.5px] leading-tight text-black/45">
                {etiquetaDeAdjunto(adjunto)}
                {fotos.length > 1 && ` · ${fotos.length} fotos`}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-[rgb(var(--brand-black-rgb)_/_0.7)] transition-colors hover:bg-neutral-200"
            >
              <X className="h-4 w-4" strokeWidth={2.25} />
            </button>
          </div>

          {/* La foto grande, CONTENIDA y no recortada: el punto de abrir es ver la
              referencia entera, incluida su forma real. */}
          <div className="flex min-h-0 flex-1 items-center justify-center bg-neutral-50 px-5 py-4">
            {foto ? (
              <img
                src={foto}
                alt={tituloDeAdjunto(adjunto)}
                className="max-h-[60vh] w-auto max-w-full rounded-xl object-contain"
              />
            ) : (
              <p className="py-16 text-[13px] text-[#3C3C43]/50">
                {cargando ? 'Cargando…' : 'Esta referencia no tiene imagen para mostrar.'}
              </p>
            )}
          </div>

          {/* La tira, sólo con más de una: con una sola sería una miniatura de la misma
              foto que está arriba. */}
          {fotos.length > 1 && (
            <div className="flex shrink-0 gap-2 overflow-x-auto px-5 py-4">
              {fotos.map((url, i) => (
                <button
                  key={url}
                  type="button"
                  onClick={() => setIndice(i)}
                  aria-label={`Foto ${i + 1} de ${fotos.length}`}
                  aria-current={i === indice}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-neutral-100 transition-all ${
                    i === indice
                      ? 'ring-2 ring-brand-primary'
                      : 'ring-1 ring-black/[0.06] opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default VisorDeAdjunto;
